/** Web 独立商品与整份设备变体录入契约；金额与币种只由 Stripe 权威回读派生。 */
import { z } from 'zod';
import { TitleI18nSchema } from './product-metadata';
import { LANGUAGE_CODE_RE } from './system-config';

export const WEB_PRODUCT_DEVICE_PLATFORMS = ['DEFAULT', 'IOS', 'ANDROID'] as const;
export type WebProductDevicePlatform = (typeof WEB_PRODUCT_DEVICE_PLATFORMS)[number];
export const WebProductDevicePlatformSchema = z.enum(WEB_PRODUCT_DEVICE_PLATFORMS);
export const WEB_STRIPE_PRODUCT_ID_PATTERN = /^prod_[A-Za-z0-9]+$/;
export const WEB_STRIPE_PRICE_ID_PATTERN = /^price_[A-Za-z0-9]+$/;
const money = z
  .string()
  .regex(/^(0|[1-9][0-9]*)$/)
  .max(19)
  .refine(
    (v) => !/^(0|[1-9][0-9]*)$/.test(v) || (v.length <= 19 && BigInt(v) <= 9223372036854775807n),
    'Exceeds int8',
  );
export const WebProductPromotionSchema = z
  .record(
    z.string().regex(LANGUAGE_CODE_RE),
    z
      .string()
      .min(1)
      .max(200)
      .refine((v) => v.trim().length > 0),
  )
  .refine((v) => Object.keys(v).length > 0 && Object.keys(v).length <= 50)
  .nullable();
/**
 * 变体可写字段（A8 §3.1.2）。Stripe 映射两列 `string | null`、同空同非空（字典 §2.6）；
 * 同空即无 Stripe 映射。服务端对无映射变体只接受同值下架，创建与其余写入仍要求映射。
 */
export const WebProductVariantRequestSchema = z
  .object({
    vendor_product_id: z
      .string()
      .min(1)
      .max(255)
      .refine((v) => v.trim().length > 0)
      .nullable(),
    stripe_price_id: z
      .string()
      .min(7)
      .max(255)
      .regex(/^price_[A-Za-z0-9_]+$/)
      .nullable(),
    subscription_period: z.enum(['WEEKLY', 'MONTHLY']).nullable(),
    coin_amount: money,
    bonus_amount: money,
    daily_grant_amount: z.literal('0'),
    original_amount_minor_units: money.nullable(),
    sort_order: z.number().int().min(-2147483648).max(2147483647),
    is_active: z.boolean(),
  })
  .strict();
export const CreateWebPaymentProductRequestSchema = z
  .object({
    access_level_id: z.string().min(1).max(255).regex(/^\S+$/),
    product_type: z.enum(['CONSUMABLE', 'SUBSCRIPTION']),
    subscription_effect: z.literal('VIP_READING').nullable(),
    name: z
      .string()
      .min(1)
      .max(100)
      .refine((v) => v.trim().length > 0),
    title_i18n: TitleI18nSchema,
    promotion_i18n: WebProductPromotionSchema.optional(),
    default_variant: WebProductVariantRequestSchema.extend({ is_active: z.literal(false) }),
  })
  .strict()
  .superRefine((v, ctx) => {
    for (const [field, pattern] of [
      ['vendor_product_id', WEB_STRIPE_PRODUCT_ID_PATTERN],
      ['stripe_price_id', WEB_STRIPE_PRICE_ID_PATTERN],
    ] as const) {
      const value = v.default_variant[field];
      if (value === null || !pattern.test(value))
        ctx.addIssue({
          code: 'custom',
          path: ['default_variant', field],
          message: 'Must use a canonical Stripe identifier',
        });
    }
    if ((v.product_type === 'SUBSCRIPTION') !== (v.subscription_effect === 'VIP_READING'))
      ctx.addIssue({
        code: 'custom',
        path: ['subscription_effect'],
        message: 'Effect must match product type',
      });
  });
export const UpdateWebPaymentProductRequestSchema = z
  .object({
    name: CreateWebPaymentProductRequestSchema.shape.name,
    title_i18n: CreateWebPaymentProductRequestSchema.shape.title_i18n,
    promotion_i18n: CreateWebPaymentProductRequestSchema.shape.promotion_i18n,
  })
  .partial()
  .strict()
  .refine((v) => Object.keys(v).length > 0);
export type WebProductVariantRequest = z.infer<typeof WebProductVariantRequestSchema>;
/** 有 Stripe 映射的变体：送 Stripe 回读核验、写入新映射时使用。 */
export type MappedWebProductVariant = WebProductVariantRequest & {
  vendor_product_id: string;
  stripe_price_id: string;
};
export type CreateWebPaymentProductRequest = z.infer<typeof CreateWebPaymentProductRequestSchema>;
export type UpdateWebPaymentProductRequest = z.infer<typeof UpdateWebPaymentProductRequestSchema>;
export type WebStripeVerificationStatus =
  | 'VERIFIED'
  | 'NOT_CHECKED'
  | 'NOT_APPLICABLE'
  | 'CONFIG_CHANGED'
  | 'BUDGET_EXHAUSTED'
  | 'DEADLINE_EXCEEDED'
  | 'CONFIG_UNAVAILABLE'
  | 'MISMATCH'
  | 'UPSTREAM_UNAVAILABLE';
/** 无 Stripe 映射变体（两列同空）列表态为 NOT_APPLICABLE、`is_serveable=false`（A8 §3.1.1）。 */
export interface WebPaymentProductVariantResponse extends WebProductVariantRequest {
  id: string;
  product_id: string;
  device_platform: WebProductDevicePlatform;
  amount_minor_units: string;
  currency: string;
  stripe_verification_status: WebStripeVerificationStatus;
  is_serveable: boolean;
}
export interface WebPaymentProductResponse {
  id: string;
  app_id: string;
  access_level_id: string;
  product_type: 'CONSUMABLE' | 'SUBSCRIPTION';
  subscription_effect: 'VIP_READING' | null;
  name: string;
  title_i18n: Record<string, string>;
  promotion_i18n: Record<string, string> | null;
  variants: WebPaymentProductVariantResponse[];
}

/** 响应 DTO 的 Zod 定义供 OpenAPI 消费，不引入数据库核验状态列。 */
export const WebPaymentProductVariantResponseSchema = WebProductVariantRequestSchema.extend({
  id: z.string().uuid(),
  product_id: z.string().uuid(),
  device_platform: WebProductDevicePlatformSchema,
  amount_minor_units: money,
  currency: z.string().regex(/^[A-Z]{3}$/),
  stripe_verification_status: z.enum([
    'VERIFIED',
    'NOT_CHECKED',
    'NOT_APPLICABLE',
    'CONFIG_CHANGED',
    'BUDGET_EXHAUSTED',
    'DEADLINE_EXCEEDED',
    'CONFIG_UNAVAILABLE',
    'MISMATCH',
    'UPSTREAM_UNAVAILABLE',
  ]),
  is_serveable: z.boolean(),
});
export const WebPaymentProductResponseSchema = z.object({
  id: z.string().uuid(),
  app_id: z.string().uuid(),
  access_level_id: CreateWebPaymentProductRequestSchema.shape.access_level_id,
  product_type: CreateWebPaymentProductRequestSchema.shape.product_type,
  subscription_effect: CreateWebPaymentProductRequestSchema.shape.subscription_effect,
  name: CreateWebPaymentProductRequestSchema.shape.name,
  title_i18n: TitleI18nSchema,
  promotion_i18n: WebProductPromotionSchema,
  variants: z.array(WebPaymentProductVariantResponseSchema),
});
